import '../product-card/product-card-display';
import aleksIcon from '../../common/icons/aleks-icon';
import emit from '../../common/events';
import invoiceIcon from '../../common/icons/invoice-icon';
import laptopIcon from '../../common/icons/laptop-icon';
import refundIcon from '../../common/icons/refund-icon';
import store from '../../store/store';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing } from "lit";
import { selectRequest, selectSelectedCatalog } from './store/search.selector';

export enum CustomResultsSelectorLayout {
  standard = 'standard',
  aleks = 'aleks',
}

export enum CustomResultsSelectorImage {
  laptop = 'laptop',
  refund = 'refund',
  aleks = 'aleks',
  invoice = 'invoice'
}

export const CustomResultsSelectorImages = {
  [CustomResultsSelectorImage.laptop]: laptopIcon,
  [CustomResultsSelectorImage.refund]: refundIcon,
  [CustomResultsSelectorImage.aleks]: aleksIcon,
  [CustomResultsSelectorImage.invoice]: invoiceIcon,
};

/**
 * A configuration object which will be conditionally rendered as
 * an icon, heading, and set of dropdowns if the listed terms match
 * and at least on catalog matches.
 */
export declare interface CustomResultOptionConfigStandard {
  img: CustomResultsSelectorImage;
  terms: Array<string>;
  layout: CustomResultsSelectorLayout.standard;
  heading: string;
  optionLists: Array<CustomResultOptionList>;
}

/**
 * A configuration object which will be conditionally rendered as
 * an icon, heading, and set of dropdowns if the listed terms match
 * and at least on catalog matches.
 */
export declare interface CustomResultOptionConfigAleks {
  img: CustomResultsSelectorImage;
  terms: Array<string>;
  layout: CustomResultsSelectorLayout.aleks;
}

/**
 * A list of options for the user to select that will be rendered as a dropdown.
 * This will only be rendered if one of the catalogs configured here matches the
 * selected catalog in redux.
 */
export declare interface CustomResultOptionList {
  catalogs: Array<Catalog>;
  singleOption?: CustomResultOption;
  label?: string;
  options?: Array<CustomResultOption>;
}

/**
 * A link that the user can select which will be embedded in a dropdown.
 */
export declare interface CustomResultOption {
  label: string;
  url: string;
}

/**
 * deafult custom search terms which will shows two banners.
 */
const CUSTOM_SEARCH_TERMS = ['lost account'];

/**
 * The default configuration object. This defines what the component displays if no configuration
 * is provided.
 */
export const DEFAULT_CUSTOM_RESULT_OPTION_CONFIG: Array<CustomResultOptionConfigStandard | CustomResultOptionConfigAleks> = [{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'access', 'access code', 'inclusive access' ],
  heading: 'Need assistance to gain access?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.unified, Catalog.products, Catalog.seg, Catalog.content, Catalog.articles ],
    label: 'Access PreK-12 Help',
    options: [{
      label: 'Locate Teacher Registration Codes',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Locate-Staff-Registration-Codes',
    }],
  }, {
    catalogs: [ Catalog.unified, Catalog.products, Catalog.he, Catalog.content, Catalog.articles ],
    label: 'Access Higher-Ed Help',
    options: [{
      label: 'Connect - Student registration, access, and account creation (for students)',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-Student-registration-access-and-account-creation-for-students',
    },{
      label: 'Connect - What is temporary access',
      url: 'https://mh.my.site.com/CXG/s/article/What-is-Temporary-Access',
    },{
      label: 'Connect - I did not receive a registration code with my text book',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-I-did-not-receive-a-registration-code-with-my-text-book',
    },{
      label: 'Connect - Resources to help you on your first day of class',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-Resources-to-help-you-on-your-first-day-of-class',
    }],
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'account', 'he account', 'k12 account', 'register', 'registration' ],
  heading: 'Need assistance to Register?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.unified, Catalog.products, Catalog.seg, Catalog.content, Catalog.articles ],
    label: 'PreK-12 Registration Help',
    options: [{
      label: 'Create a McGraw Hill Teacher Account',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Create-a-Teacher-Account',
    }, {
      label: 'Create a McGraw Hill Student Account',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Create-a-Student-Account',
    }],
  }, {
    catalogs: [ Catalog.unified, Catalog.products, Catalog.he, Catalog.content, Catalog.articles ],
    label: 'Higher-Ed Registration Help',
    options: [{
      label: 'Connect - Resources to help you on your first day of class',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-Resources-to-help-you-on-your-first-day-of-class',
    },{
      label: 'Connect - Why am I unable to register for self study?',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-Why-am-I-unable-to-register-for-self-study',
    },{
      label: 'Connect - I did not receive a registration code with my text book',
      url: 'https://mh.my.site.com/CXG/s/article/Connect-I-did-not-receive-a-registration-code-with-my-text-book',
    },{
      label: 'I lost my registration code',
      url: 'https://mh.my.site.com/CXG/s/article/I-lost-my-registration-code',
    }],
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'import', 'students', 'import students' ],
  heading: 'Need help importing students?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.seg ],
    label: 'Student Import Resources',
    options: [{
      label: 'How do I Import Students?',
      url: 'https://mh.my.site.com/DTS/s/article/ConnectED-How-do-I-Import-Students',
    }, {
      label: 'Student Import Best Practices',
      url: 'https://mh.my.site.com/DTS/s/article/Student-Import-Best-Practices',
    }],
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'classroom' ],
  heading: 'Need help with Google Classroom?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.seg ],
    singleOption: {
      label: 'Google Classroom Help',
      url: 'https://mh.my.site.com/DTS/s/article/Google-Classroom-Resources',
    },
  }]
},{
  img: CustomResultsSelectorImage.invoice,
  terms: [ 'invoice', 'receipt' ],
  heading: 'Need a copy of your invoice?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.unified, Catalog.products, Catalog.seg, Catalog.he, Catalog.content, Catalog.articles ],
    singleOption: {
      label: 'Request an Invoice',
      url: 'https://mh.my.site.com/CSOM/s/invoice-copy-request',
    },
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'order status' ],
  heading: 'Want to know the status of your order?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.unified, Catalog.products, Catalog.seg, Catalog.he, Catalog.content, Catalog.articles ],
    singleOption: {
      label: 'Check Order Status',
      url: 'https://mh.my.site.com/CSOM/s/order-status',
    },
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'lost' ],
  heading: 'Need help accessing your teacher or student account?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.seg ],
    label: 'Login Help',
    options: [{
      label: 'Forgot Username or Password',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Forgot-Username-or-Password'
    },{
      label: 'Access your Teacher Account',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Teacher-Login'
    },{
      label: 'Access your Student Account',
      url: 'https://mh.my.site.com/DTS/s/article/My-mheducation-com-Student-Login'
    },]
  }]
},{
  img: CustomResultsSelectorImage.laptop,
  terms: [ 'redemption code', 'redemption' ],
  heading: 'Need help distributing student editions?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.seg ],
    label: 'Redemption Code Information',
    options: [{
      label: 'How do I Create a New Redemption Code?',
      url: 'https://mh.my.site.com/DTS/s/article/ConnectED-How-do-I-Create-a-New-Redemption-Code'
    },{
      label: 'Options for Distributing Student Editions',
      url: 'https://mh.my.site.com/DTS/s/article/ConnectED-Options-for-Distributing-Student-Editions'
    }]
  }]
},{
  img: CustomResultsSelectorImage.refund,
  terms: [ 'refund', 'return' ],
  heading: 'Need to request a refund?',
  layout: CustomResultsSelectorLayout.standard,
  optionLists: [{
    catalogs: [ Catalog.unified, Catalog.products, Catalog.seg, Catalog.he, Catalog.content, Catalog.articles ],
    singleOption: {
      label: 'Request a Refund',
      url: 'https://mh.my.site.com/CSOM/s/website-product-return',
    }
  }]
},{
  img: CustomResultsSelectorImage.aleks,
  terms: [ '0021321957', '1935485105', '0021450293', '1935485113', '0021324875', '1935485121', '0021457611', '0021362076', '1935485024', '0021324034', '0021321957', '1935485032', '0021407037', '0021381445', '0021381216', '1935485040', '0021380503', '1935485156', '1935485083', '0021450625', '1935485067', '0021447551', '0021449872', '1935485091', '1935485164', '1935485172', '1935485148', '9781935485100', '9780021450299', '9781935485117', '9780021324873', '9781935485124', '9780021457618', '9780021362073', '9781935485025', '9780021324033', '9780021321957', '9781935485032', '9780021407033', '9780021381449', '9780021381210', '9781935485049', '9780021380503', '9781935485155', '9781935485087', '9780021450626', '9781935485063', '9780021447558', '9780021449873', '9781935485094', '9781935485162', '9781935485179', '9781935485148' ],
  layout: CustomResultsSelectorLayout.aleks,
}];

/**
 * Allows the user to select from a list of custom results based on the search term and selected catalog.
 * 
 * @csspart container
 * @csspart icon
 * @csspart heading
 * @csspart options
 * @csspart option-menu-button
 * @csspart option-menu-* Where '*" is the list of catalogs concatenated together
 * @csspart option-list
 * @csspart option-link
 * 
 * @event click - Event name: custom-result-selector-option. This event fires when the user selects an option from the dropdown menu.
 * @event click - Event name: custom-result-selector-show. This event fires when the user opens or closes a dropdown menu.
 * @event custom-result-selector-show - This event fires when the content is displayed because of a matching search term and selected catalog
 */
@customElement('mhecomm-search-custom-result-selector')
export class SearchCustomResultSelector extends connect(store)(BaseComponent) {

  /**
   * The configuration object that defines the content that is shown
   */
  @property()
  configs: Array<CustomResultOptionConfigStandard | CustomResultOptionConfigAleks> = DEFAULT_CUSTOM_RESULT_OPTION_CONFIG;

  /**
   * The currently selected catalog
   */
  @state()
  selectedCatalog: Catalog = Catalog.unified;

  /**
   * The current search term
   */
  @state()
  searchTerm?: string;

  /**
   * The active configuration array object contianing a icon, heading, and dropdown information
   */
  @state()
  activeConfigs: Array<CustomResultOptionConfigStandard | CustomResultOptionConfigAleks> = [];

  /**
   * The matching options lists with active config which will be rendered as dropdowns
   */
  @state()
  matchingOptionLists: Array<CustomResultOptionList> = [];

  /**
   * Whether or not the content is currently being displayed
   */
  @state()
  isShown: boolean = false;

  renderComp() {
    return html`
      ${this.activeConfigs?.map((activeConfig, activeConfigIndex) => html`
        ${ activeConfig && (activeConfig.layout !== CustomResultsSelectorLayout.standard || this.matchingOptionLists[activeConfig.heading].length > 0) ? html`
          <div part="container" class="d-flex flex-column flex-lg-row align-items-center justify-content-center mb-3 bg-light p-3 rounded">
            <div part="icon" class="d-none d-lg-block">${CustomResultsSelectorImages[activeConfig.img]}</div>
            ${ activeConfig.layout === CustomResultsSelectorLayout.aleks ? html`
              <div part="options" class="ms-lg-4 mt-3 mt-lg-0">
                <div>You can <a href="https://www.aleks.com/k12/order_now" target="_blank" aria-label="${ariaLabelWithNewTab('Visit ALEKS website to purchase this product')}">purchase this product on ALEKS.com.</a></div>
                <div class="mt-1">For more information on ALEKS, <a href="https://www.mheducation.com/prek-12/program/microsites/MKTSP-GAB02M0.html">visit our website.</a></div>
              </div>
            `: ''}
            ${ activeConfig.layout === CustomResultsSelectorLayout.standard ? html`
              <div part="heading" class="fs-4 ms-lg-4">${activeConfig.heading}</div>
              <div part="options" class="ms-lg-4 mt-3 mt-lg-0">
                ${this.matchingOptionLists[activeConfig.heading].map((optionList, index) => html`
                  ${optionList.options?.length && optionList.options?.length > 0 ? html`
                    <div class="dropdown" role="group" part="dropdown">
                      <button
                        part="option-menu-button"
                        class="btn btn-lg btn-outline-primary dropdown-toggle w-100 ${index !== 0 ? 'mt-3' : ''}"
                        type="button"
                        id="customResultSelector${activeConfigIndex+index}"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-target="#customResultDropdown${activeConfigIndex+index}"
                        @click=${(event: any) => this._handleDropdownToggle(optionList, activeConfigIndex+index, event)}>
                        ${optionList.label}
                      </button>
                      <ul part="option-menu-${optionList.catalogs.map(catalog => catalog.toLowerCase()).join('-')}" class="dropdown-menu" aria-labelledby="customResultSelector${activeConfigIndex+index}" id="customResultDropdown${activeConfigIndex+index}">
                        ${optionList.options?.map(option => html`
                          <li part="option-list">
                            <a
                              part="option-link"
                              class="dropdown-item" href="${option.url}"
                              @click=${() => this._handleOptionClick(option)}
                              target="_blank"
                              aria-label="${ariaLabelWithNewTab(option.label)}">
                              ${option.label}
                            </a>
                          </li>
                        `)}
                      </ul>
                    </div>
                  `: ''}
                  ${optionList.singleOption ? html`
                    <a class="btn btn-lg btn-outline-primary"
                      part="option-link"
                      href="${optionList.singleOption.url}"
                      @click=${() => this._handleOptionClick(optionList.singleOption as CustomResultOption)}
                      target="_blank"
                      aria-label="${ariaLabelWithNewTab(optionList.singleOption.label)}">
                      ${optionList.singleOption.label}
                    </a>
                  `: ''}
                `)}
              </div>
            `: ''}
          </div>
        `: nothing}
      `)}
    `;
  }

  /**
   * Logs an event for when a custom result option is clicked by the user.
   * 
   * @param option The selected option.
   */
  private _handleOptionClick(option: CustomResultOption) {
    emit({
      element: this,
      detail: {
        eventName: 'custom-result-selector-option',
        option,
      },
      name: 'click',
    });
  }

  /**
   * Opens or closes the option list dropdown and logs a corresponding event.
   *
   * @param optionList The optionList that is being toggled
   * @param index The index of the dropdown
   * @param event The event object
   */
  private _handleDropdownToggle(optionList: CustomResultOptionList, index: number, event: any) {
    if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
      const customResultDropdowns = this.shadowRoot!.querySelectorAll(`.dropdown-menu`);
      customResultDropdowns?.forEach((element) => element.setAttribute('class', 'dropdown-menu'));

      const customResultDropdown = this.shadowRoot!.querySelector(`#customResultDropdown${index}`) as HTMLLinkElement;
      const action = customResultDropdown.classList.contains('show') ? 'closing' : 'opening';
      customResultDropdown.classList.toggle('show');
      this.addEventListener('focusout', () => {
        customResultDropdown.setAttribute('class', 'dropdown-menu');
      });

      emit({
        element: this,
        detail: {
          eventName: 'custom-result-selector-dropdown',
          optionList,
          action,
        },
        name: 'click',
      });
    }
  }
  
  /**
   * Determines if the content should be shown by looking at the
   * search term and selected catalog. If it should be shown it
   * finds which config object should be rendered.
   *
   * @param state The current snapshot of the redux store state
   */
  stateChanged(state: StoreState) {
    this.selectedCatalog = selectSelectedCatalog(state) as Catalog;
    this.searchTerm = selectRequest(state)?.searchTerms;
    let activeConfigs: Array<CustomResultOptionConfigStandard | CustomResultOptionConfigAleks> = [];
    if(this.searchTerm && CUSTOM_SEARCH_TERMS.includes(this.searchTerm.toLowerCase())) {
      const searchTerms = this.searchTerm.split(' ');
      searchTerms.forEach(searchTerm => {
        if(this.getActiveConfig(searchTerm)!) {
          activeConfigs.push(this.getActiveConfig(searchTerm)!);
        }
      });
    } else {
      if(this.getActiveConfig(this.searchTerm)) {
        activeConfigs = [this.getActiveConfig(this.searchTerm)!];
      }
    }
    this.activeConfigs = activeConfigs;

    const show = !! (this.activeConfigs[0] && (this.activeConfigs[0].layout !== CustomResultsSelectorLayout.standard || this.matchingOptionLists[this.activeConfigs[0].heading].length > 0));

    // If the content was hidden and is now being revealed, log an event.
    if (!this.isShown && show) {
      emit({
        element: this,
        detail: {
          activeConfig: this.activeConfigs,
        },
        name: 'custom-result-selector-show',
      });
    }

    this.isShown = show;
  }

  private getActiveConfig(searchTerm) {
    const activeConfig = this.configs.find(config => {
      const termMatches = !! config.terms.find(term => term.toLowerCase() === searchTerm?.toLowerCase());
      const hasMatchingOptionList = !! (config.layout !== CustomResultsSelectorLayout.standard || config.optionLists.find(optionList => optionList.catalogs.includes(this.selectedCatalog)));
      return termMatches && (hasMatchingOptionList);
    });
    if (activeConfig?.layout === CustomResultsSelectorLayout.standard) {
      this.matchingOptionLists[activeConfig.heading] = activeConfig?.optionLists.filter(optionList => optionList.catalogs.includes(this.selectedCatalog)) ?? [];
    } else {
      this.matchingOptionLists = [];
    }
    return activeConfig;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-custom-result-selector': SearchCustomResultSelector;
  }
}
